import { useEffect, useState } from 'react';
import { Column, Modal, Row } from '@simplywallst/ui-core';
import { AuthenticationSelectExperiments } from '@/components/AuthenticationSelectExperiments';
import { useIntersectionObserverDisabler } from '@/context/IntersectionObserverDisabler';
import loadable from '@loadable/component';
import { CloseIcon, Container } from './styled';
import { ActionsPanel } from './components/ActionsPanel';
import { SplashPanelRegister } from './components/SplashPanelRegister';
import { SplashPanelSignIn } from './components/SplashPanelSignIn';
import type { Method } from '@/components/AuthenticationSelect';
import { useSummaryV2 } from '@/hooks/useCompany';

const CareersUpsell = loadable(() => import('@components/CareersUpsell'), {
  resolveComponent: (component) => component.CareersUpsell,
});

export interface Props {
  onClose?: () => void;
  canClose?: boolean;
  removeWindowScroll?: boolean;
  initialType?: Method;
  onRegisterSuccess?: () => void;
  onLoginSuccess?: () => void;
}

export const GuestRegistrationModal = ({
  initialType = 'oauth',
  removeWindowScroll = false,
  onClose = () => undefined,
  canClose = false,
  onRegisterSuccess,
  onLoginSuccess,
}: Props) => {
  const [type, setType] = useState(initialType);
  const [showCareersUpsell, setShowCareersUpsell] = useState(false);
  const intersectionObserverDisabler = useIntersectionObserverDisabler();
  const { tickerSymbol } = useSummaryV2();

  useEffect(() => {
    if (typeof intersectionObserverDisabler.setEnabled === 'function') {
      /** we'll turn off intersection observer IF modal is shown */
      intersectionObserverDisabler.setEnabled(false);
    }
    return () => {
      if (typeof intersectionObserverDisabler.setEnabled === 'function') {
        intersectionObserverDisabler.setEnabled(true);
      }
    };
  }, [intersectionObserverDisabler]);

  return (
    <>
      <Modal
        onTamper={() => setShowCareersUpsell(true)}
        isNaked
        asForm
        blurTargetId={'root'}
        removeWindowScroll={removeWindowScroll}
        onClose={onClose}
      >
        <Row justifyContent="center" width="100%">
          <Column
            alignSelf="flex-start"
            alignSelfLg="center"
            justifyContent="center"
            displayFlex
          >
            <Container data-cy-id="guest-registration-modal">
              {canClose ? (
                <CloseIcon data-cy-id="close-button" onClick={onClose} />
              ) : null}
              <Column as="section" col={12} colLg={7}>
                {type === 'oauth' ? (
                  <SplashPanelRegister />
                ) : (
                  <SplashPanelSignIn />
                )}
              </Column>
              <Column as="section" col={12} colLg={5}>
                <ActionsPanel>
                  <AuthenticationSelectExperiments
                    ticker={tickerSymbol}
                    current={type}
                    onChangeType={(type) => setType(type)}
                    onRegisterSuccess={onRegisterSuccess}
                    onLoginSuccess={onLoginSuccess}
                  />
                </ActionsPanel>
              </Column>
            </Container>
          </Column>
        </Row>
      </Modal>
      {showCareersUpsell ? <CareersUpsell /> : null}
    </>
  );
};
